import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp, BookOpen } from "lucide-react";
import type { HubState, Taxonomy, GeneratedTaxonomy } from "@/lib/learnerHub/types";
import { getLearnerUnitMap } from "@/lib/learnerHub/utils";
import { DEMO_COURSE, unitById } from "@/lib/learnerHub/constants";

interface Props {
  state: HubState;
  setHub: (partial: Partial<HubState>) => void;
  taxonomyLibrary: Taxonomy[];
}

function confidenceBadgeClass(confidence: string): string {
  switch (confidence) {
    case "HIGH": return "bg-green-100 text-green-800 border-green-200";
    case "MEDIUM": return "bg-amber-100 text-amber-800 border-amber-200";
    case "LOW": return "bg-red-100 text-red-800 border-red-200";
    default: return "";
  }
}

export default function CompetencyMapTab({ state, setHub, taxonomyLibrary }: Props) {
  const { learners, selectedLearnerId, showDemoCourse, taxonomy } = state;
  const selectedLearner = learners.find((l) => l.id === selectedLearnerId) || null;
  const unitMap = selectedLearner ? getLearnerUnitMap(selectedLearner) : {};
  const isGenerated = "generated" in taxonomy && (taxonomy as GeneratedTaxonomy).generated;

  return (
    <div className="space-y-6">
      {/* Taxonomy selector (shown when library has more than one entry) */}
      {taxonomyLibrary.length > 1 && (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm font-medium whitespace-nowrap">Taxonomy:</label>
          <select
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
            value={taxonomy.customerId}
            onChange={(e) => {
              const t = taxonomyLibrary.find((x) => x.customerId === e.target.value);
              if (t) setHub({ taxonomy: t });
            }}
          >
            {taxonomyLibrary.map((t) => (
              <option key={t.customerId} value={t.customerId}>
                {t.name}{"generated" in t && (t as GeneratedTaxonomy).generated ? " (AI)" : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {isGenerated && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          This is an AI-generated taxonomy and has not been reviewed by subject matter experts.
          Learner completion coverage uses UCL qualifications and will not match SK-IDs.
        </p>
      )}

      {/* Learner selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium whitespace-nowrap">Learner:</label>
        <select
          className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm flex-1 max-w-xs"
          value={selectedLearnerId || ""}
          onChange={(e) => setHub({ selectedLearnerId: e.target.value || null })}
        >
          <option value="">— select a learner —</option>
          {learners.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} ({l.employeeId})
            </option>
          ))}
        </select>
      </div>

      {/* Demo course card — always visible */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <CardTitle className="text-sm">Demo course: {DEMO_COURSE.name}</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => setHub({ showDemoCourse: !showDemoCourse })}
            >
              {showDemoCourse ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
          <CardDescription className="text-xs">
            Built in Distilled Intuition · AI-extracted competency units
          </CardDescription>
        </CardHeader>
        {showDemoCourse && (
          <CardContent className="space-y-4 pt-0">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">Modules</p>
              <ol className="space-y-1">
                {DEMO_COURSE.modules.map((m, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-muted-foreground w-5 shrink-0">{i + 1}.</span>
                    {m}
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                Extracted UCL units
              </p>
              <div className="space-y-2">
                {DEMO_COURSE.extractedUnits.map((eu) => {
                  const unit = unitById(eu.unitId);
                  return (
                    <div key={eu.unitId} className="flex items-start gap-2 text-sm">
                      <Badge
                        variant="outline"
                        className={`text-xs shrink-0 mt-0.5 ${confidenceBadgeClass(eu.confidence)}`}
                      >
                        {eu.confidence}
                      </Badge>
                      <div>
                        <span className="font-mono font-medium text-xs">{eu.unitId}</span>
                        {unit && <span className="text-muted-foreground ml-1.5">· {unit.label}</span>}
                        <p className="text-xs text-muted-foreground italic mt-0.5">{eu.evidence}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Taxonomy tree */}
      {selectedLearner ? (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">
            {taxonomy.name} coverage — {selectedLearner.name}
          </h3>
          {taxonomy.processes.map((process) => (
            <Card key={process.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{process.label}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                {process.qualifications.map((qual) => (
                  <div key={qual.id}>
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      {qual.label}
                      {qual.level != null && <span className="ml-1">· Level {qual.level}</span>}
                    </p>
                    <div className="space-y-1.5">
                      {qual.units.map((unit) => {
                        const hit = unitMap[unit.id];
                        return (
                          <div
                            key={unit.id}
                            className="flex items-start gap-2.5 text-sm py-1 px-2 rounded hover:bg-muted/40"
                          >
                            {hit ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <span className={hit ? "" : "text-muted-foreground"}>
                                <span className="font-mono text-xs mr-1.5">{unit.id}</span>
                                {unit.label}
                              </span>
                              {hit && (
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${confidenceBadgeClass(hit.confidence)}`}
                                  >
                                    {hit.confidence}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground truncate">
                                    via {hit.courseName}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-8">
          Select a learner to see their competency coverage across the CCM-Eleva taxonomy.
        </p>
      )}
    </div>
  );
}
